/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import type {
    StrapiDownloadPage,
    StrapiDownloadPreviewPage,
    StrapiGlobals,
} from "@/types/strapi";

import { Spacing } from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { Text } from "@/ui/atoms/text";
import { DownloadGroup } from "@/ui/molecules/download_group";
import { DownloadMobileForm } from "@/ui/molecules/download_mobile_form";
import { RichText } from "@/ui/molecules/rich_text";

import { useGlobalsContext } from "@/util/context/globals_context";
import { buildStylesByBreakpoint } from "@/util/style_util";

type Props = {
    globalsData: StrapiGlobals;
    isDownloadPreviewPage?: true;
    pageData: StrapiDownloadPage | StrapiDownloadPreviewPage;
};

export function DownloadPageTemplate(props: Props) {
    /**
     * Context
     */
    const { isTouchDevice, operatingSystem = "mac" } = useGlobalsContext();

    /**
     * Show the sign-up form instead of the download option on mobile,
     * where downloading does not make sense. However, some Windows desktops
     * support touch input, so we exclude Windows to avoid false positives.
     */
    const showMobileSignUpForm = isTouchDevice && operatingSystem !== "windows";

    const subheadlineStyles = css(
        buildStylesByBreakpoint("marginTop", {
            extraSmall: Spacing["spacing-3"],
            medium: Spacing["spacing-8"],
        }),
        buildStylesByBreakpoint("textAlign", {
            extraSmall: "left",
            medium: "center",
        }),
    );

    /**
     * Rendering
     */
    return (
        <GridContainer marginBottom="DefaultSectionMargin">
            <GridColumn
                columnSpan={{
                    extraSmall: "full",
                    medium: 10,
                }}
                columnStart={{
                    extraSmall: 1,
                    medium: 2,
                }}
                marginBottom="DefaultSectionMargin"
                marginTop="DefaultSectionMargin"
            >
                <Text
                    fontSize="HeadlineDefault"
                    tag="h1"
                    textAlign={{
                        extraSmall: "left",
                        medium: "center",
                    }}
                >
                    {props.pageData.Headline}
                </Text>

                {props.pageData.Subheadline && (
                    <RichText
                        className={subheadlineStyles}
                        config="UiSubheadline"
                    >
                        {props.pageData.Subheadline}
                    </RichText>
                )}
            </GridColumn>

            {showMobileSignUpForm && <DownloadMobileForm />}

            <DownloadGroup
                downloadGroups={props.pageData.Downloads}
                isDownloadPreviewPage={props.isDownloadPreviewPage}
            />
        </GridContainer>
    );
}
